import React, { FC, lazy, useContext } from 'react'
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'
import ROUTES from './utils/routes'
import { PrivateRoute } from './PrivateRoute'

import { AppLayout } from './AppLayout'
import { AdminIssueHeader } from './Admin/AdminIssueHeader'
import { Intercom } from './Intercom'
import { FourOhFour, FiveHundred, NotLoggedIn } from './Errors'
import { AsciiArt } from './AsciiArt'
import { SplitContext } from './utils/SplitContext'
import { LeadForm } from './LeadForm'
import { MissingInfoModal } from './Modals/MissingInfoModal'
import { AccountBilling, AccountSettings, SubscriptionSelectPage, PaymentPage } from './Account'
import { UpgradeCTAModal, PlatformPreviewModal, ExpiredCTAModal } from './Modals'
import { Milk } from './Milk'
import { Logout } from './User'
import { AboutPage } from './AboutPage'
import { IssueContainer as IssueView } from './IssueView'
import { IssueListView } from './IssueListView'
import { Dashboard } from './Dashboard'
import { CaptionLibrary } from './CaptionLibraryView'
import { DraftsView } from './DraftsView'
import { CourseLibraryView } from './CourseLibraryView'
import { LessonView } from './LessonView'
import { CoursePreviewView } from './CoursePreviewView'
import { CheckoutView } from './CheckoutView'
import { ReceiptView } from './ReceiptView'
import { RegisterView } from './RegisterView'
import { SignInView } from './SignInView'
import { ForgotPasswordModal } from './Modals/ForgotPasswordModal'
import { ResetPasswordView } from './ResetPasswordView'
import { CourseRegisterView } from './CourseRegisterView'
import { OnboardingView } from './InformationalOnboarding'
import { AffiliateTrack } from './Affiliates/AffiliateTrack'
import { AffiliateDashboard } from './Affiliates/AffiliateDashboard'
import { TagManager } from './TagManager'
import { StoriesView } from './StoriesView'
import { Challenge } from './Challenge'
import { NewPostTour } from './tours'
import { CommunityView } from './CommunityView'

const AdminLayout = lazy(() => import('./Admin/AdminLayout/AdminLayout'))
const Gallery = lazy(() => import('./GalleryView/GalleryView'))

const AppRouter: FC = () => {
  const {
    isReady,
    isEnabled,
    treatments: {}
  } = useContext(SplitContext)

  return (
    <Router>
      <Intercom />
      <Milk />
      <AsciiArt />
      <MissingInfoModal />
      <ExpiredCTAModal />
      <UpgradeCTAModal />
      <PlatformPreviewModal />
      <OnboardingView />
      <TagManager />
      <Challenge />

      <Switch>
        <PrivateRoute
          userRole={['admin']}
          path="/admin/:path?"
          isLazyLoaded
          component={AdminLayout}
        />

        <Route path={ROUTES.affiliate} component={AffiliateTrack} exact />

        <Route path={ROUTES.user.signup} component={RegisterView} exact />
        <Route path={ROUTES.user.signupCourse} component={CourseRegisterView} />
        <Route path={[ROUTES.user.resetPassword, ROUTES.user.login]}>
          <Route path={ROUTES.user.resetPassword} component={ForgotPasswordModal} />
          <SignInView />
        </Route>
        <Route path={ROUTES.user.confirmCode} exact component={ResetPasswordView} />

        <Route path={ROUTES.leads.form} component={LeadForm} />
        <Route path={ROUTES.user.logout} component={Logout} />

        <Route path={ROUTES.errors.fiveHundred} component={FiveHundred} />
        <Route path={ROUTES.errors.notFound} component={FourOhFour} />
        <Route path={ROUTES.errors.notLoggedIn} component={NotLoggedIn} />

        {/* Community */}
        <PrivateRoute
          userRole={['admin', 'basic', 'course']}
          path={ROUTES.dashboard.community}
          component={CommunityView}
        />

        <Route path="*">
          <AppLayout>
            <NewPostTour>
              <Switch>
                {/* Dashboard */}
                <PrivateRoute
                  userRole={['admin', 'basic']}
                  path={ROUTES.dashboard.home}
                  exact
                  component={Dashboard}
                />
                {/* Caption Library */}
                <PrivateRoute
                  userRole={['admin', 'basic']}
                  path={ROUTES.captions}
                  component={CaptionLibrary}
                  exact
                />
                {/* Stories */}
                <PrivateRoute
                  userRole={['admin', 'basic']}
                  path={[ROUTES.stories.story, ROUTES.stories.list]}
                  component={StoriesView}
                  exact
                />
                {/* Gallery */}
                <PrivateRoute
                  userRole={['admin', 'basic']}
                  path={[
                    ROUTES.gallery.main,
                    ROUTES.gallery.favorites,
                    ROUTES.gallery.collectionPage,
                    ROUTES.gallery.collections,
                    ...ROUTES.gallery.photoPage
                  ]}
                  component={Gallery}
                  isLazyLoaded
                  exact
                />
                {/* Drafts */}
                <PrivateRoute
                  userRole={['admin', 'basic']}
                  exact
                  path={ROUTES.dashboard.drafts}
                  component={DraftsView}
                />
                {/* Action Plans */}
                <PrivateRoute
                  userRole={['admin', 'basic']}
                  exact
                  path={ROUTES.dashboard.issues}
                  component={IssueListView}
                />
                {/* Course Library */}
                <PrivateRoute
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.dashboard.courses}
                  component={CourseLibraryView}
                  exact
                />
                {/* Checkout */}
                <PrivateRoute
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.checkout}
                  component={CheckoutView}
                  exact
                />
                {/* Receipt */}
                <PrivateRoute
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.receipt}
                  component={ReceiptView}
                />
                {/* Account Settings */}
                <PrivateRoute
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.user.settings.profile}
                  exact
                  component={AccountSettings}
                />
                {/* Account Billing */}
                <PrivateRoute
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.user.settings.billing}
                  exact
                  component={AccountBilling}
                />
                {/* Account Plan List */}
                <PrivateRoute
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.user.settings.plan}
                  exact
                  component={SubscriptionSelectPage}
                />
                {/* Account Payment Step */}
                <PrivateRoute
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.user.settings.payment}
                  exact
                  component={PaymentPage}
                />
                {/* Affiliate Dashboard */}
                <PrivateRoute
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.affiliates.dashboard}
                  component={AffiliateDashboard}
                />

                {/* Issue */}
                <Route path={ROUTES.issues.view}>
                  <>
                    {/* Admin Issue Header */}
                    <PrivateRoute
                      userRole={['admin', 'basic', 'course']}
                      path={ROUTES.issues.view}
                      component={AdminIssueHeader}
                    />
                    {/* Issue View */}
                    <PrivateRoute
                      userRole={['admin', 'basic', 'course']}
                      path={ROUTES.issues.view}
                      component={IssueView}
                    />
                  </>
                </Route>

                {/* Course Preview */}
                <Route
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.courses.preview}
                  component={CoursePreviewView}
                />

                {/* Lesson */}
                <PrivateRoute
                  userRole={['admin', 'basic', 'course']}
                  path={ROUTES.courses.lesson}
                  component={LessonView}
                />

                {/* Legacy or not completed */}
                <Route path={ROUTES.dashboard.aboutJasmine} component={AboutPage} />

                {/* Redirect to 404 */}
                <Route path="*">
                  <Redirect to={ROUTES.errors.notFound} />
                </Route>
              </Switch>
            </NewPostTour>
          </AppLayout>
        </Route>
      </Switch>
    </Router>
  )
}

export default AppRouter
